import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AUTH_CONFIGURED,
  buildLoginUrl,
  initiateLoginWithPKCE,
  clearSession,
  isLoggedIn,
} from './api';
import BlogsSection from './components/blogs/BlogsSection';
import Seo from './components/common/Seo';
import { SITE_NAME } from './constants/site';

/*
 * Admin shell.
 *
 * There is no client-side login form and no unauthenticated path in any build: if
 * the session is missing or expired the browser goes straight to the Cognito
 * hosted UI, which returns to /callback with an authorization code. Callback.js
 * exchanges it for the JWT, and every write from here carries that JWT as
 * `Authorization: Bearer <token>`. The backend is the real authority — this guard
 * only avoids showing a UI that cannot save anything.
 */
function Admin() {
  const navigate = useNavigate();
  const [state, setState] = useState('checking');

  useEffect(() => {
    if (isLoggedIn()) {
      setState('open');
      return;
    }
    if (!AUTH_CONFIGURED) {
      // Redirecting to a half-configured Cognito URL produces an opaque error
      // page; say what is actually missing instead.
      setState('unconfigured');
      return;
    }
    clearSession();
    // Start login. Prefer PKCE — initiateLoginWithPKCE generates a code_verifier,
    // stores it for Callback.js, and returns a hosted-UI URL carrying the
    // challenge. Falls back to the plain code grant if Web Crypto is unavailable.
    (async () => {
      try {
        window.location.href = await initiateLoginWithPKCE();
      } catch (err) {
        window.location.href = buildLoginUrl();
      }
    })();
  }, []);

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  if (state === 'checking') {
    return (
      <div className="admin-checking">
        <Seo title="Admin" path="/admin" noIndex />
        <h1>Checking authorisation…</h1>
      </div>
    );
  }

  if (state === 'unconfigured') {
    return (
      <div className="admin-checking">
        <Seo title="Admin" path="/admin" noIndex />
        <h1>Admin is not configured</h1>
        <p>
          Publishing needs an identity provider. Set <code>REACT_APP_COGNITO_DOMAIN</code>{' '}
          and <code>REACT_APP_COGNITO_CLIENT_ID</code> in <code>.env</code>, and make sure
          this app's callback URL is registered with it.
        </p>
        <p>
          <Link className="btn btn-ghost" to="/">
            Back to the site
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Seo title="Admin" path="/admin" noIndex />

      <div className="admin-panel-header">
        <h1>{SITE_NAME} Admin</h1>
        <div className="admin-panel-header-actions">
          <Link className="btn btn-ghost" to="/" target="_blank" rel="noopener noreferrer">
            View site
          </Link>
          <button type="button" className="btn btn-danger" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>

      <BlogsSection />
    </div>
  );
}

export default Admin;
